import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, UserCog, Save } from 'lucide-react'
import { api, messaggioErrore } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { Avatar } from '../../components/ui/Avatar'
import { EmptyState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import { useAuthStore } from '../../stores/authStore'
import { etichettaPiano, variantePiano, formatDataOra } from '../../lib/format'
import type { Utente, Ruolo, Piano } from '../../lib/tipi'
import pagina from '../pagine.module.css'

interface Dettaglio {
  utente: Utente
  team: { id: string; nome: string } | null
  sessioni_recenti: Array<{
    id: string
    durataSecondi: number
    votoComplessivo: number | null
    createdAt: string
  }>
}

export function AdminUtenteDettaglio() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const entra = useAuthStore((s) => s.entra)

  const [modaleEdit, setModaleEdit] = useState(false)

  const q = useQuery({
    queryKey: ['admin', 'utente', id],
    queryFn: async () => (await api.get<{ dati: Dettaglio }>(`/superadmin/utenti/${id}`)).data.dati,
    enabled: !!id,
  })

  const impersona = useMutation({
    mutationFn: async () =>
      (await api.post<{ dati: { token: string; utente: Utente } }>(`/superadmin/utenti/${id}/impersona`)).data.dati,
    onSuccess: (data) => {
      if (!data) return
      entra(data.token, data.utente)
      const ruolo = data.utente.ruolo
      navigate(
        ruolo === 'superadmin' ? '/admin/dashboard'
        : ruolo === 'manager' ? '/manager/dashboard'
        : '/app/dashboard',
      )
    },
    onError: (err) => alert(messaggioErrore(err)),
  })

  if (q.isLoading) return <div className={pagina.skeleton}>Caricamento…</div>
  if (q.error || !q.data) return <EmptyState titolo="Utente non trovato" />

  const u = q.data.utente
  const team = q.data.team

  return (
    <div className={pagina.wrap}>
      <div className={pagina.header}>
        <Button variante="ghost" taglia="sm" iconaSinistra={<ArrowLeft size={14} />} onClick={() => navigate(-1)}>
          Indietro
        </Button>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variante="secondary" iconaSinistra={<UserCog size={14} />} onClick={() => setModaleEdit(true)}>
            Modifica
          </Button>
          <Button
            variante="primary"
            onClick={() => {
              if (confirm(`Impersonare ${u.nome} ${u.cognome}? Verrà generato un token di 30 minuti.`)) {
                impersona.mutate()
              }
            }}
          >
            Impersona
          </Button>
        </div>
      </div>

      <Card>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <Avatar nome={u.nome} cognome={u.cognome} taglia="lg" />
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <h2 style={{ margin: 0, fontSize: 20 }}>{u.nome} {u.cognome}</h2>
              <Badge variante={u.ruolo === 'superadmin' ? 'accent' : u.ruolo === 'manager' ? 'info' : 'default'}>
                {u.ruolo}
              </Badge>
              <Badge variante={variantePiano(u.piano)}>{etichettaPiano(u.piano)}</Badge>
              {u.attivo ? <Badge variante="success">Attivo</Badge> : <Badge variante="warning">Sospeso</Badge>}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px', maxWidth: 600 }}>
              <div>📧 {u.email}</div>
              <div>📞 <span className="mono">{u.telefono ?? '—'}</span></div>
              <div>🎯 Sessioni totali: <strong>{u.sessioniTotali}</strong></div>
              <div>📅 Iscritto il {formatDataOra(u.createdAt)}</div>
              {team && (
                <div>👥 Team: <strong>{team.nome}</strong></div>
              )}
              {u.lastLogin && (
                <div>🕒 Ultimo accesso: {formatDataOra(u.lastLogin)}</div>
              )}
            </div>
          </div>
        </div>
      </Card>

      <Card titolo="Sessioni recenti">
        {q.data.sessioni_recenti.length === 0 ? (
          <EmptyState titolo="Nessuna sessione" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {q.data.sessioni_recenti.map((s) => (
              <div
                key={s.id}
                onClick={() => navigate(`/app/sessioni/${s.id}`)}
                style={{
                  padding: '10px 14px',
                  background: 'var(--bg-subtle)',
                  borderRadius: 'var(--radius-md)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{formatDataOra(s.createdAt)}</div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <span className="mono" style={{ fontSize: 13 }}>{Math.floor(s.durataSecondi / 60)}m</span>
                  {s.votoComplessivo != null && (
                    <span className="mono" style={{ fontWeight: 600 }}>{s.votoComplessivo}/10</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <ModaleModificaUtente
        aperto={modaleEdit}
        utente={u}
        onChiudi={() => setModaleEdit(false)}
        onSalvato={() => {
          setModaleEdit(false)
          queryClient.invalidateQueries({ queryKey: ['admin', 'utente', id] })
        }}
      />
    </div>
  )
}

function ModaleModificaUtente({ aperto, utente, onChiudi, onSalvato }:
  { aperto: boolean; utente: Utente; onChiudi: () => void; onSalvato: () => void }) {
  const [ruolo, setRuolo] = useState<Ruolo>(utente.ruolo)
  const [piano, setPiano] = useState<Piano>(utente.piano)
  const [attivo, setAttivo] = useState(utente.attivo)
  const [errore, setErrore] = useState<string | null>(null)
  const [caricamento, setCaricamento] = useState(false)

  const salva = async () => {
    setErrore(null)
    setCaricamento(true)
    try {
      await api.put(`/superadmin/utenti/${utente.id}`, { ruolo, piano, attivo })
      onSalvato()
    } catch (err) {
      setErrore(messaggioErrore(err))
    } finally {
      setCaricamento(false)
    }
  }

  return (
    <Modal aperto={aperto} titolo="Modifica utente" onChiudi={onChiudi}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Ruolo</label>
          <select className={pagina.select} value={ruolo} onChange={(e) => setRuolo(e.target.value as Ruolo)} style={{ width: '100%', height: 40, marginTop: 4 }}>
            <option value="sdr">SDR</option>
            <option value="manager">Manager</option>
            <option value="superadmin">Superadmin</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Piano</label>
          <select className={pagina.select} value={piano} onChange={(e) => setPiano(e.target.value as Piano)} style={{ width: '100%', height: 40, marginTop: 4 }}>
            <option value="free">Free</option>
            <option value="pro">Pro</option>
            <option value="team">Team</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={attivo} onChange={(e) => setAttivo(e.target.checked)} />
          <span>Account attivo</span>
        </label>
        {errore && (
          <div style={{ padding: 10, background: 'var(--danger-soft)', color: 'var(--danger)', borderRadius: 'var(--radius-md)', fontSize: 13 }}>
            {errore}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variante="ghost" onClick={onChiudi}>Annulla</Button>
          <Button iconaSinistra={<Save size={14} />} onClick={salva} caricamento={caricamento}>Salva</Button>
        </div>
      </div>
    </Modal>
  )
}

